import { cn } from "../lib/cn";

/**
 * One icon set: **outline, 1.5px stroke, 16px by default** (doc 40 §8 — the
 * sidebar's nav rows carry a 16px outline icon).
 *
 * Hand-drawn on a 24px grid rather than pulled from an icon library, for two
 * reasons. First, the reference app ships ~1,500 lucide glyphs to use forty of
 * them, and doc 27 §8 puts a 250 KB budget on the initial route. Second, the
 * status silhouettes below have to be distinguishable *in shape alone, in
 * greyscale* (doc 40 §14 — colour is never the only signal), and that is a
 * design decision per glyph rather than a library default.
 *
 * (H) Every icon here is `aria-hidden` unless given a `title`. An icon is
 * decoration next to a label; where it is the only carrier of meaning it needs
 * a name, and the type makes you say so.
 */
const PATHS = {
  /* ── form affordances ──────────────────────────────────────────────────── */
  //  Drawn for the sign-in fields. `eye`/`eye-off` are not decoration: they are
  //  the control that reveals a typed password, which is the one place a person
  //  cannot check their own input any other way.
  mail: (
    <>
      <rect x="3.25" y="5.25" width="17.5" height="13.5" rx="2" />
      <path d="M3.75 7l8.25 6 8.25-6" />
    </>
  ),
  lock: (
    <>
      <rect x="4.75" y="10.25" width="14.5" height="9.5" rx="2" />
      <path d="M8.25 10.25V7.5a3.75 3.75 0 017.5 0v2.75" />
    </>
  ),
  eye: (
    <>
      <path d="M2.75 12S6 6.25 12 6.25 21.25 12 21.25 12 18 17.75 12 17.75 2.75 12 2.75 12z" />
      <circle cx="12" cy="12" r="2.75" />
    </>
  ),
  "eye-off": (
    <>
      <path d="M6.3 6.6C4.1 8.1 2.75 12 2.75 12s3.25 5.75 9.25 5.75c1.5 0 2.8-.36 3.9-.9" />
      <path d="M9.9 9.9a2.75 2.75 0 003.9 3.9" />
      <path d="M17.4 15.2c1.4-1.2 2.35-2.6 2.85-3.2 0 0-3.25-5.75-9.25-5.75-.55 0-1.07.05-1.56.14" />
      <path d="M4 4l16 16" />
    </>
  ),

  /* ── status silhouettes: deliberately different outlines ───────────────── */
  "check-circle": (
    <>
      <circle cx="12" cy="12" r="8.25" />
      <path d="M8.5 12.2l2.4 2.4 4.6-5" />
    </>
  ),
  triangle: (
    <>
      <path d="M12 4.5l8 14H4l8-14z" />
      <path d="M12 10v4" />
      <path d="M12 16.4h.01" />
    </>
  ),
  octagon: (
    <>
      <path d="M8.6 3.75h6.8l4.85 4.85v6.8l-4.85 4.85H8.6L3.75 15.4V8.6L8.6 3.75z" />
      <path d="M9.5 9.5l5 5m0-5l-5 5" />
    </>
  ),
  "info-circle": (
    <>
      <circle cx="12" cy="12" r="8.25" />
      <path d="M12 11v5.5" />
      <path d="M12 7.9h.01" />
    </>
  ),
  circle: <circle cx="12" cy="12" r="8.25" />,
  /** A realised loss: a struck-through square, unmistakable at 16px. */
  "square-slash": (
    <>
      <rect x="4.25" y="4.25" width="15.5" height="15.5" rx="2" />
      <path d="M7.75 16.25l8.5-8.5" />
    </>
  ),

  /* ── chrome ────────────────────────────────────────────────────────────── */
  "chevron-down": <path d="M6 9.5l6 6 6-6" />,
  "chevron-up": <path d="M6 14.5l6-6 6 6" />,
  "chevron-right": <path d="M9.5 6l6 6-6 6" />,
  "chevron-left": <path d="M14.5 6l-6 6 6 6" />,
  "arrow-right": (
    <>
      <path d="M4.5 12h15" />
      <path d="M13.5 6l6 6-6 6" />
    </>
  ),
  close: <path d="M6.5 6.5l11 11m0-11l-11 11" />,
  check: <path d="M5 12.5l4.5 4.5L19 7" />,
  minus: <path d="M5.5 12h13" />,
  plus: <path d="M12 5.5v13M5.5 12h13" />,
  search: (
    <>
      <circle cx="10.5" cy="10.5" r="6" />
      <path d="M15 15l4.5 4.5" />
    </>
  ),
  filter: <path d="M4 6h16l-6.2 7.3v5.2l-3.6-1.8v-3.4L4 6z" />,
  columns: (
    <>
      <rect x="3.75" y="4.75" width="16.5" height="14.5" rx="1.5" />
      <path d="M9.25 4.75v14.5M14.75 4.75v14.5" />
    </>
  ),
  keyboard: (
    <>
      <rect x="2.75" y="6.75" width="18.5" height="10.5" rx="1.5" />
      <path d="M6.5 10h.01M9.5 10h.01M12.5 10h.01M15.5 10h.01M18 10h.01M7.5 13.75h9" />
    </>
  ),
  external: (
    <>
      <path d="M14 4.75h5.25V10" />
      <path d="M19.25 4.75L11 13" />
      <path d="M18 14v4.5a.75.75 0 0 1-.75.75H5.75A.75.75 0 0 1 5 18.5V6.75A.75.75 0 0 1 5.75 6H10" />
    </>
  ),
  bell: (
    <>
      <path d="M6.5 10a5.5 5.5 0 0 1 11 0c0 3.2.75 4.6 1.5 5.5H5c.75-.9 1.5-2.3 1.5-5.5z" />
      <path d="M10 18.5a2.2 2.2 0 0 0 4 0" />
    </>
  ),
  help: (
    <>
      <circle cx="12" cy="12" r="8.25" />
      <path d="M9.75 9.4a2.3 2.3 0 1 1 2.9 2.2c-.5.2-.8.6-.8 1.1v.6" />
      <path d="M12 16.4h.01" />
    </>
  ),
  cog: (
    <>
      <circle cx="12" cy="12" r="2.75" />
      <path d="M12 3.75v2.1M12 18.15v2.1M4.6 7.85l1.85 1.05M17.55 15.1l1.85 1.05M4.6 16.15l1.85-1.05M17.55 8.9l1.85-1.05" />
    </>
  ),
  sparkles: (
    <>
      <path d="M12 4l1.4 3.9L17.3 9.3l-3.9 1.4L12 14.6l-1.4-3.9L6.7 9.3l3.9-1.4L12 4z" />
      <path d="M18 15.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7.7-1.8z" />
    </>
  ),
  megaphone: (
    <>
      <path d="M4.75 10.5v3a1.25 1.25 0 0 0 1.25 1.25h1.5l8.5 4.25V5L7.5 9.25H6a1.25 1.25 0 0 0-1.25 1.25z" />
      <path d="M18.75 9.5a3.5 3.5 0 0 1 0 5" />
    </>
  ),
  refresh: (
    <>
      <path d="M19.25 12a7.25 7.25 0 1 1-2.6-5.55" />
      <path d="M19.25 4.75V9h-4.25" />
    </>
  ),
  logout: (
    <>
      <path d="M10 19.25H5.75A.75.75 0 0 1 5 18.5v-13a.75.75 0 0 1 .75-.75H10" />
      <path d="M15 8.5l3.5 3.5L15 15.5" />
      <path d="M18.5 12H9.5" />
    </>
  ),

  /* ── domain nav ────────────────────────────────────────────────────────── */
  dashboard: (
    <>
      <rect x="3.75" y="3.75" width="7" height="7" rx="1.25" />
      <rect x="13.25" y="3.75" width="7" height="7" rx="1.25" />
      <rect x="3.75" y="13.25" width="7" height="7" rx="1.25" />
      <rect x="13.25" y="13.25" width="7" height="7" rx="1.25" />
    </>
  ),
  queue: (
    <>
      <path d="M4 6.5l1.75 1.75L9 5" />
      <path d="M4 15.5l1.75 1.75L9 14" />
      <path d="M12 7h8M12 16h8" />
    </>
  ),
  claim: (
    <>
      <path d="M13.5 3.75H6.75A.75.75 0 0 0 6 4.5v15a.75.75 0 0 0 .75.75h10.5a.75.75 0 0 0 .75-.75V8.25l-4.5-4.5z" />
      <path d="M13.25 3.9V8.5h4.5" />
      <path d="M9 13h6M9 16.25h4" />
    </>
  ),
  money: (
    <>
      <rect x="2.75" y="6.25" width="18.5" height="11.5" rx="1.5" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6 12h.01M18 12h.01" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.25" />
      <path d="M12 7.75V12l3 1.9" />
    </>
  ),
  chart: (
    <>
      <path d="M4 20V4" />
      <path d="M4 20h16" />
      <path d="M8 17V12M12.5 17V8M17 17v-3.5" />
    </>
  ),
  "trend-up": (
    <>
      <path d="M4 16.5l5.25-5.25 3.25 3.25L20 7.5" />
      <path d="M15.25 7.5H20v4.75" />
    </>
  ),
  "trend-down": (
    <>
      <path d="M4 7.5l5.25 5.25 3.25-3.25L20 16.5" />
      <path d="M15.25 16.5H20v-4.75" />
    </>
  ),
  users: (
    <>
      <circle cx="9.5" cy="8.25" r="3" />
      <path d="M3.75 19.25a5.75 5.75 0 0 1 11.5 0" />
      <path d="M16 6.1a3 3 0 0 1 0 5.9" />
      <path d="M17.25 14.5a5.2 5.2 0 0 1 3 4.75" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3.75l7 2.5v5.1c0 4-2.8 7.3-7 8.9-4.2-1.6-7-4.9-7-8.9V6.25l7-2.5z" />
      <path d="M9.25 12l1.9 1.9 3.6-3.9" />
    </>
  ),
  robot: (
    <>
      <rect x="4.25" y="8" width="15.5" height="11.25" rx="2" />
      <path d="M12 4.75V8" />
      <circle cx="9.25" cy="13" r="1" />
      <circle cx="14.75" cy="13" r="1" />
      <path d="M10 16.25h4" />
    </>
  ),
  /** A day on a schedule. Distinct from `clock`, which is a deadline. */
  calendar: (
    <>
      <rect x="3.75" y="5.25" width="16.5" height="15" rx="2" />
      <path d="M3.75 9.75h16.5" />
      <path d="M8.25 3.75v3M15.75 3.75v3" />
    </>
  ),
  /** A page of text. Post arriving at a billing office, not a data grid. */
  /**
   * Download — an arrow into a tray.
   *
   * Added for the CSV exports (GAP-083). `document` was the nearest existing glyph and it
   * reads as "open this", which is the opposite of what the control does: the file lands on
   * the person's disk and leaves the building. A control whose icon says the wrong thing is
   * a control people press without meaning to.
   */
  download: (
    <>
      <path d="M12 3.75v10.5" />
      <path d="M8.25 10.5 12 14.25l3.75-3.75" />
      <path d="M4.75 15.75v3a1.5 1.5 0 0 0 1.5 1.5h11.5a1.5 1.5 0 0 0 1.5-1.5v-3" />
    </>
  ),
  document: (
    <>
      <path d="M6.25 3.75h7l4.5 4.5v11.5a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5V4.25a.5.5 0 0 1 .5-.5z" />
      <path d="M13.25 3.75v4.5h4.5" />
      <path d="M8.75 13h6.5M8.75 16h4.5" />
    </>
  ),
  /* ── navigation (v3) ───────────────────────────────────────────────────────
   *  One distinct glyph per destination, so the rail stops repeating the same
   *  banknote down Revenue cycle. Geometry follows lucide (ISC licence) on the
   *  same 24px grid and 1.5px stroke as the rest of the set; added by hand, not
   *  as a dependency, for the reason in this file's header. */
  "arrow-left": <path d="M19 12H5M11 18l-6-6 6-6" />,
  receipt: (
    <>
      <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" />
      <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
      <path d="M12 17.5v-11" />
    </>
  ),
  code: (
    <>
      <path d="m16 18 6-6-6-6" />
      <path d="m8 6-6 6 6 6" />
    </>
  ),
  scale: (
    <>
      <path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
      <path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
      <path d="M7 21h10M12 3v18M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2" />
    </>
  ),
  inbox: (
    <>
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </>
  ),
  wallet: (
    <>
      <path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" />
      <path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" />
    </>
  ),
  undo: (
    <>
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
    </>
  ),
  "file-minus": (
    <>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4M9 15h6" />
    </>
  ),
  landmark: (
    <>
      <path d="M3 22h18M6 18v-7M10 18v-7M14 18v-7M18 18v-7" />
      <path d="M12 2 20 7H4Z" />
    </>
  ),
  send: (
    <>
      <path d="m22 2-7 20-4-9-9-4Z" />
      <path d="M22 2 11 13" />
    </>
  ),
  "clipboard-check": (
    <>
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="m9 14 2 2 4-4" />
    </>
  ),
  forward: (
    <>
      <path d="m15 17 5-5-5-5" />
      <path d="M4 18v-2a4 4 0 0 1 4-4h12" />
    </>
  ),
  building: (
    <>
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
      <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
      <path d="M10 6h4M10 10h4M10 14h4M10 18h4" />
    </>
  ),
  "map-pin": (
    <>
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </>
  ),
  stethoscope: (
    <>
      <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 12 0V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" />
      <path d="M8 15v1a6 6 0 0 0 12 0v-4" />
      <circle cx="20" cy="10" r="2" />
    </>
  ),
  upload: (
    <>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="m17 8-5-5-5 5M12 3v12" />
    </>
  ),
  "list-checks": (
    <>
      <path d="m3 17 2 2 4-4M3 7l2 2 4-4" />
      <path d="M13 6h8M13 12h8M13 18h8" />
    </>
  ),
  tag: (
    <>
      <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" />
      <circle cx="7.5" cy="7.5" r="1" />
    </>
  ),
  book: <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />,
  network: (
    <>
      <rect x="16" y="16" width="6" height="6" rx="1" />
      <rect x="2" y="16" width="6" height="6" rx="1" />
      <rect x="9" y="2" width="6" height="6" rx="1" />
      <path d="M5 16v-3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3M12 12V8" />
    </>
  ),
  sliders: (
    <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M2 14h4M10 8h4M18 16h4" />
  ),
  message: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  gauge: (
    <>
      <path d="m12 14 4-4" />
      <path d="M3.34 19a10 10 0 1 1 17.32 0" />
    </>
  ),
  "mail-check": (
    <>
      <path d="M22 13V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12c0 1.1.9 2 2 2h8" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7M16 19l2 2 4-4" />
    </>
  ),
} as const;

export type IconName = keyof typeof PATHS;

export interface IconProps {
  name: IconName;
  /** 4 = 16px (the default and the nav size), 5 = 20px, 6 = 24px, 7 = 28px. */
  size?: 4 | 5 | 6 | 7;
  className?: string;
  /** Set only when the icon is the sole carrier of meaning; normally it is not. */
  title?: string;
}

const SIZE: Record<NonNullable<IconProps["size"]>, string> = {
  4: "h-4 w-4",
  5: "h-5 w-5",
  6: "h-6 w-6",
  7: "h-7 w-7",
};

export function Icon({ name, size = 4, className, title }: IconProps) {
  return (
    <svg
      className={cn(SIZE[size], "shrink-0", className)}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {title ? <title>{title}</title> : null}
      {PATHS[name]}
    </svg>
  );
}
